// Settings card (office and admin, Session ATZ, email system Phase 2, Item 6): East London Elevators' own email addresses.
//
// THE ONE LIST. Every part of the email system reads it (the recorder, the reply decision, the Enquiries queue, the reply
// library and the comparison of what was sent with what was drafted): a message from one of these addresses is ELE's own,
// so a conversation answered from adrian@ reads as answered and its reply is learnt as the office's voice, and the engine
// never drafts a reply to ELE itself. The connected mailbox always counts, whatever the list says, so it is shown but
// cannot be removed. It is NOT the list a reply may be sent from: that is Gmail's own verified send-as list.
//
// Each add or remove saves at once (a remove and the reset are two-step confirms), and every change is audited.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { EleAddressesSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";

const ADDRESS_PATTERN = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;

export function EleAddressesSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<EleAddressesSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.settings.eleAddresses.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function save(next: string[] | null, which: string, done: string): Promise<boolean> {
    setBusy(which);
    setError(null);
    setMessage(null);
    try {
      setView(await api.settings.eleAddresses.put(next));
      setMessage(done);
      return true;
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the list. Please try again in a moment."));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function add() {
    const address = adding.trim().toLowerCase();
    if (!view || !address) return;
    if (!ADDRESS_PATTERN.test(address)) {
      setError("Give one address on its own, such as accounts@eastlondonelevators.co.uk, with no name or angle brackets.");
      return;
    }
    if (view.addresses.includes(address) || view.connectedMailboxes.includes(address)) {
      setError(`${address} is already on the list.`);
      return;
    }
    if (await save([...view.addresses, address], "add", `Added ${address}.`)) setAdding("");
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Our own email addresses">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Our own email addresses</h2>
      <p className="mb-3 text-sm text-ele-body">
        Mail from these addresses is ours. A conversation answered from any of them counts as answered, its reply is learnt as the
        office&apos;s own, and no reply is ever drafted to them. Add an address the office replies from; remove one that is no longer ours.
        This is not the list replies can be sent from: Gmail&apos;s own settings decide that.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the list.
        </p>
      ) : (
        <div className="space-y-3">
          <ul className="space-y-1" aria-label="Our own addresses">
            {view.connectedMailboxes.map((a) => (
              <li key={`m-${a}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-ele-strong [overflow-wrap:anywhere]">{a}</span>
                <span className="text-xs text-ele-body">The connected mailbox: always ours</span>
              </li>
            ))}
            {view.addresses
              .filter((a) => !view.connectedMailboxes.includes(a))
              .map((a) => (
                <li key={a} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-ele-strong [overflow-wrap:anywhere]">{a}</span>
                  <ConfirmButton
                    onConfirm={() => void save(view.addresses.filter((x) => x !== a), `remove:${a}`, `Removed ${a}.`)}
                    label="Remove"
                    confirmLabel="Remove it? Press again"
                    busy={busy === `remove:${a}`}
                    busyLabel="Removing..."
                    disabled={busy != null}
                    className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                  />
                </li>
              ))}
          </ul>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-[min(100%,16rem)] flex-1 flex-col gap-1">
              <span className="font-menu text-xs uppercase tracking-wide text-ele-body">Add an address</span>
              <input
                type="email"
                aria-label="Add an address"
                value={adding}
                onChange={(e) => {
                  setAdding(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void add();
                  }
                }}
                placeholder="accounts@eastlondonelevators.co.uk"
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
              />
            </label>
            <button
              type="button"
              onClick={() => void add()}
              disabled={busy != null || !adding.trim()}
              className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:text-ele-green disabled:opacity-50"
            >
              {busy === "add" ? "Adding..." : "Add address"}
            </button>
          </div>
          {view.customised && (
            <ConfirmButton
              onConfirm={() => void save(null, "reset", "Restored the built-in list.")}
              label="Restore the built-in list"
              confirmLabel="Restore it? Press again"
              busy={busy === "reset"}
              busyLabel="Restoring..."
              disabled={busy != null}
              className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
            />
          )}
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
